import {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type FC,
	type ReactElement,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useLedgerStepRealtime } from "../hooks/useLedgerStepRealtime";
import { useReadOnlyAccess } from "../hooks/useReadOnlyAccess";
import { LedgerActions } from "../redux/ledgerSanitization/ledgerSanitization.slice";
import {
	confirmDatasetStatsThunk,
	getPerfDataFromDataRange,
	getStaticDataFromDataRange,
	uploadFileThunk,
} from "../redux/ledgerSanitization/ledgerSanitization.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../redux/store";
import { services } from "../services";
import {
	DatasetStepEnum,
	type DatasetCreationTypeEnum,
	type TActiveDatasetMeta,
} from "../types/ledger.type";
import { getApiErrorMessage } from "../utils/apiErrorUtils";
import { getDatasetSanitizationSteps } from "../utils/ledgerUtils";
import { toCurrentTenantPath } from "../utils/tenantUtils";

const TAPES_LOADING_MSG =
	"Your tapes are still loading. This can take a few minutes; select Next Step again once they finish.";
const TAPES_LOAD_FAILED_FALLBACK_MSG =
	"Loading the tapes failed.";

export type TLedgerContext = {
	activeStep: DatasetStepEnum | null;
	setActiveStep: React.Dispatch<
		React.SetStateAction<DatasetStepEnum | null>
	>;
	navigate: ReturnType<typeof useNavigate>;
	dispatch: ReturnType<typeof useAppDispatch>;
	sheet: string;
	setSheet: React.Dispatch<React.SetStateAction<string>>;
	headerRange: string;
	setHeaderRange: React.Dispatch<
		React.SetStateAction<string>
	>;
	dataRange: string;
	setDataRange: React.Dispatch<
		React.SetStateAction<string>
	>;
	uploadDataset: (file: File) => void;
	staticDatasetUploadStatus: {
		status:
			| "not-started"
			| "in-progress"
			| "completed"
			| "failed";
		error: any;
		loading: boolean;
	};
	perfDatasetUploadStatus: {
		status:
			| "not-started"
			| "in-progress"
			| "completed"
			| "failed";
		error: any;
		loading: boolean;
	};
	activeDatasetMeta: TActiveDatasetMeta | null;
	disableContinue: boolean;
	setDisableContinue: React.Dispatch<
		React.SetStateAction<boolean>
	>;
	loadingContinue: boolean;
	setLoadingContinue: React.Dispatch<
		React.SetStateAction<boolean>
	>;
	isDataRangeModified: boolean;
	setIsDataRangeModified: React.Dispatch<
		React.SetStateAction<boolean>
	>;
	isUserInputChanged: boolean;
	setIsUserInputChanged: React.Dispatch<
		React.SetStateAction<boolean>
	>;
	shouldDisableEverything: boolean;
	isReadOnlyUser: boolean;
	handleContinue: () => void;
	moveToNextStep: () => void;
};

export const LedgerContext =
	createContext<TLedgerContext | null>(null);

export const LedgerContextProvider: FC<{
	children: ReactElement;
}> = ({ children }) => {
	const [activeStep, setActiveStep] =
		useState<DatasetStepEnum | null>(null);

	const {
		activeDatasetMeta,
		datasetSanitizationMeta,
		staticDatasetUploadStatus,
		staticDataRangeSelection,
		perfDatasetUploadStatus,
		perfDataRangeSelection,
		submissionStatus,
		specsQuestions,
		method,
		reviewStats,
		tapesLoad,
	} = useAppSelector((state) => state.ledger);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isReadOnlyUser = useReadOnlyAccess();

	const datasetStepMapping = useMemo(() => {
		return getDatasetSanitizationSteps(
			method as DatasetCreationTypeEnum
		);
	}, [method]);

	const [sheet, setSheet] = useState<string>("");
	const [headerRange, setHeaderRange] =
		useState<string>("");
	const [dataRange, setDataRange] = useState<string>("");

	const [isDataRangeModified, setIsDataRangeModified] =
		useState<boolean>(false);
	const [isUserInputChanged, setIsUserInputChanged] =
		useState(false);

	const [disableContinue, setDisableContinue] =
		useState<boolean>(true);

	const [loadingContinue, setLoadingContinue] =
		useState<boolean>(false);

	const uploadDataset = (file: File) => {
		if (isReadOnlyUser) {
			return;
		}
		if (!activeDatasetMeta) {
			return;
		}

		const isStatic =
			activeStep === DatasetStepEnum.STEP_STATIC_UPLOAD;

		void dispatch(
			uploadFileThunk({
				file,
				datasetId: activeDatasetMeta.datasetId,
				isStatic,
			})
		);
	};

	useEffect(() => {
		if (activeStep === DatasetStepEnum.STEP_STATIC_UPLOAD) {
			setDisableContinue(true);
			if (
				staticDatasetUploadStatus.status === "completed"
			) {
				toast.success("Static dataset uploading completed");
				setDisableContinue(false);
			}
		} else if (
			activeStep === DatasetStepEnum.STEP_PERF_UPLOAD
		) {
			setDisableContinue(true);
			if (perfDatasetUploadStatus.status === "completed") {
				toast.success("Perf dataset uploading completed");
				setDisableContinue(false);
			}
		} else if (
			activeStep ===
				DatasetStepEnum.STEP_STATIC_DATA_RANGE ||
			activeStep === DatasetStepEnum.STEP_PERF_DATA_RANGE
		) {
			// Reset states
			setSheet("");
			setHeaderRange("");
			setDataRange("");
			setIsDataRangeModified(false);
			setIsUserInputChanged(false);
		} else if (
			activeStep === DatasetStepEnum.STEP_DATASET_SPECS
		) {
			setDisableContinue(false);
		} else if (
			activeStep === DatasetStepEnum.STEP_DATASET_APPROVAL
		) {
			setDisableContinue(
				Boolean(reviewStats.error) ||
					reviewStats.data?.status !== "COMPLETED"
			);
		} else if (
			activeStep === DatasetStepEnum.STEP_DATASET_COMPLETE
		) {
			setDisableContinue(false);
		}
	}, [
		activeStep,
		staticDatasetUploadStatus,
		perfDatasetUploadStatus,
		specsQuestions,
		method,
		reviewStats.error,
		reviewStats.data?.screen,
		reviewStats.data?.status,
	]);

	useEffect(() => {
		if (
			staticDataRangeSelection?.recentRangeSelection &&
			staticDataRangeSelection.suggestedRange
		) {
			if (
				staticDataRangeSelection.recentRangeSelection
					.sheet !==
					staticDataRangeSelection.suggestedRange
						.sheetName ||
				staticDataRangeSelection.recentRangeSelection
					.headerRange !==
					staticDataRangeSelection.suggestedRange
						.headerRange ||
				staticDataRangeSelection.recentRangeSelection
					.dataRange !==
					staticDataRangeSelection.suggestedRange.bodyRange
			) {
				setIsDataRangeModified(true);
			} else if (
				staticDataRangeSelection.isModifiedNotConfirmed
			) {
				setIsDataRangeModified(true);
			} else {
				if (isUserInputChanged) {
					setIsDataRangeModified(true);
				} else setIsDataRangeModified(false);
			}
		}
	}, [
		staticDataRangeSelection?.recentRangeSelection,
		staticDataRangeSelection?.suggestedRange,
		staticDataRangeSelection?.isStepConfirmed,
		staticDataRangeSelection?.isModifiedNotConfirmed,
		isUserInputChanged,
	]);

	useEffect(() => {
		if (
			perfDataRangeSelection?.recentRangeSelection &&
			perfDataRangeSelection.suggestedRange
		) {
			if (
				perfDataRangeSelection.recentRangeSelection
					.sheet !==
					perfDataRangeSelection.suggestedRange.sheetName ||
				perfDataRangeSelection.recentRangeSelection
					.headerRange !==
					perfDataRangeSelection.suggestedRange
						.headerRange ||
				perfDataRangeSelection.recentRangeSelection
					.dataRange !==
					perfDataRangeSelection.suggestedRange.bodyRange
			) {
				setIsDataRangeModified(true);
			} else if (
				perfDataRangeSelection.isModifiedNotConfirmed
			) {
				setIsDataRangeModified(true);
			} else {
				if (isUserInputChanged) {
					setIsDataRangeModified(true);
				} else setIsDataRangeModified(false);
			}
		}
	}, [
		perfDataRangeSelection?.recentRangeSelection,
		perfDataRangeSelection?.suggestedRange,
		perfDataRangeSelection?.isStepConfirmed,
		perfDataRangeSelection?.isModifiedNotConfirmed,
		isUserInputChanged,
	]);

	const validateInput = useCallback(
		(
			sheet: string,
			headerRange: string,
			dataRange: string
		) => {
			const rangeRegex = /^[A-Za-z]+\d+:[A-Za-z]+\d+$/;

			if (sheet.length === 0) {
				// Invalid sheet name
				// Handle the error or display a message
				toast.error("Sheet name is not valid");
				return false;
			}

			if (!rangeRegex.test(headerRange)) {
				// Invalid header range
				// Handle the error or display a message
				toast.error("Header range is not valid");
				return false;
			}

			if (!rangeRegex.test(dataRange)) {
				// Invalid data range
				// Handle the error or display a message
				toast.error("Data range is not valid");
				return false;
			}

			// All inputs are valid
			return true;
		},
		[]
	);

	const reloadDataset = useCallback(async () => {
		if (isReadOnlyUser) {
			return;
		}
		if (
			activeDatasetMeta &&
			sheet &&
			headerRange &&
			dataRange
		) {
			const isValid = validateInput(
				sheet,
				headerRange,
				dataRange
			);
			if (isValid) {
				if (
					activeStep ===
					DatasetStepEnum.STEP_STATIC_DATA_RANGE
				) {
					await dispatch(
						getStaticDataFromDataRange({
							datasetId: activeDatasetMeta.datasetId,
							sheet: sheet.trim(),
							headerRange: headerRange.trim(),
							dataRange: dataRange.trim(),
							isStepConfirmed: false,
							isReload: true,
						})
					);
				} else if (
					activeStep ===
					DatasetStepEnum.STEP_PERF_DATA_RANGE
				) {
					await dispatch(
						getPerfDataFromDataRange({
							datasetId: activeDatasetMeta.datasetId,
							sheet: sheet.trim(),
							headerRange: headerRange.trim(),
							dataRange: dataRange.trim(),
							isStepConfirmed: false,
							isReload: true,
						})
					);
				}
			}
		} else {
			toast.error(
				"Please enter sheet, header and data range"
			);
		}
	}, [
		activeDatasetMeta,
		activeStep,
		sheet,
		headerRange,
		dataRange,
		validateInput,
		dispatch,
		isReadOnlyUser,
	]);

	const confirmSpecsChanges = useCallback(async () => {
		const activeDatasetId = activeDatasetMeta?.datasetId;
		try {
			if (!activeDatasetId) return;
			const data =
				await services.ledgerService.confirmSpecs(
					activeDatasetId
				);

			if (
				data.is_success ||
				data.status === "COMPLETED" ||
				data.current_step
			) {
				toast.success("Dataset specs confirmed");
				// Return success from here then call meta and then move to next
				return {
					moveToNextStep: true,
				};
			} else {
				return {
					moveToNextStep: false,
				};
			}
		} catch (error) {
			console.error(error);
			toast.error(
				getApiErrorMessage(
					error,
					"Error confirming metadata"
				)
			);
			return {
				moveToNextStep: false,
			};
		}
	}, [activeDatasetMeta]);

	const moveToNextStep = useCallback(async () => {
		if (activeDatasetMeta && datasetSanitizationMeta) {
			setLoadingContinue(true);
			const updatedCurrentStep =
				await services.ledgerService.getDatasetMeta(
					activeDatasetMeta.datasetId
				);

			if (updatedCurrentStep) {
				dispatch(
					LedgerActions.updateCurrentStep(
						updatedCurrentStep
					)
				);
				// an upload_ug ledger holds on its last tape step while the tapes load, so Continue can't move yet
				const continueTapesLoad =
					updatedCurrentStep.tapes_load;
				if (continueTapesLoad?.status === "IN_PROGRESS") {
					toast.info(TAPES_LOADING_MSG);
				} else if (continueTapesLoad?.status === "ERROR") {
					toast.error(
						continueTapesLoad.error_msg ??
							TAPES_LOAD_FAILED_FALLBACK_MSG
					);
				}
				const nextStepPath =
					datasetStepMapping[
						updatedCurrentStep.current_step
					].path;
				if (nextStepPath) {
					setIsDataRangeModified(false);
					navigate(
						toCurrentTenantPath(
							`/add-dataset/${activeDatasetMeta.datasetId}${nextStepPath}`
						)
					);
				}
			}
			setTimeout(() => {
				setLoadingContinue(false);
			}, 500);
		}
	}, [
		activeDatasetMeta,
		datasetSanitizationMeta,
		dispatch,
		datasetStepMapping,
		navigate,
	]);

	const isSyncingRef = useRef(false);

	const syncTapesLoad = useCallback(async () => {
		if (!activeDatasetMeta || isSyncingRef.current) return;
		isSyncingRef.current = true;
		try {
			const meta =
				await services.ledgerService.getDatasetMeta(
					activeDatasetMeta.datasetId
				);
			if (meta.tapes_load?.status === tapesLoad?.status)
				return;
			dispatch(LedgerActions.updateCurrentStep(meta));

			// advance current_step once the push completes
			const nextStep =
				datasetSanitizationMeta?.allSteps.find(
					(el) => el.stepId === meta.current_step
				)?.stepId;
			const hasStepAdvanced =
				Boolean(nextStep) && nextStep !== activeStep;
			const canAutoAdvance =
				!isReadOnlyUser &&
				!loadingContinue &&
				datasetSanitizationMeta?.currentStep.stepId ===
					activeStep;
			if (nextStep && hasStepAdvanced && canAutoAdvance) {
				navigate(
					toCurrentTenantPath(
						`/add-dataset/${activeDatasetMeta.datasetId}${datasetStepMapping[nextStep].path}`
					)
				);
			}
		} finally {
			isSyncingRef.current = false;
		}
	}, [
		activeDatasetMeta,
		activeStep,
		datasetSanitizationMeta,
		datasetStepMapping,
		dispatch,
		isReadOnlyUser,
		loadingContinue,
		navigate,
		tapesLoad?.status,
	]);

	const handleTapesLoadComplete = useCallback(
		(data: {
			status: string;
			error_msg?: string | null;
		}) => {
			if (data.status === "error") {
				toast.error(
					data.error_msg ?? TAPES_LOAD_FAILED_FALLBACK_MSG
				);
			}
			void syncTapesLoad();
		},
		[syncTapesLoad]
	);

	const hasConnectedRef = useRef(false);
	const handleRealtimeConnected = useCallback(() => {
		if (!hasConnectedRef.current) {
			hasConnectedRef.current = true;
			return;
		}
		void syncTapesLoad();
	}, [syncTapesLoad]);

	useLedgerStepRealtime({
		datasetId: activeDatasetMeta?.datasetId ?? "",
		step: activeStep ?? "",
		onConnected: handleRealtimeConnected,
		onTapesLoadComplete: handleTapesLoadComplete,
		realtimeService: services.realtimeServiceInstance,
		enabled: Boolean(
			activeDatasetMeta?.datasetId &&
				activeStep &&
				tapesLoad?.status === "IN_PROGRESS"
		),
	});

	const handleContinue = useCallback(async () => {
		if (isReadOnlyUser) {
			return;
		}
		if (activeDatasetMeta && activeStep) {
			const activeDatasetId = activeDatasetMeta.datasetId;
			setLoadingContinue(true);
			switch (activeStep) {
				case DatasetStepEnum.STEP_STATIC_DATA_RANGE:
					if (
						staticDataRangeSelection?.recentRangeSelection &&
						isDataRangeModified
					) {
						if (isUserInputChanged) {
							await reloadDataset();
						} else {
							await dispatch(
								getStaticDataFromDataRange({
									datasetId: activeDatasetId,
									...staticDataRangeSelection.recentRangeSelection,
									isStepConfirmed: true,
								})
							);
						}
					}
					break;

				case DatasetStepEnum.STEP_PERF_DATA_RANGE:
					if (
						perfDataRangeSelection?.recentRangeSelection &&
						isDataRangeModified
					) {
						if (isUserInputChanged) {
							await reloadDataset();
						} else {
							await dispatch(
								getPerfDataFromDataRange({
									datasetId: activeDatasetId,
									...perfDataRangeSelection.recentRangeSelection,
									isStepConfirmed: true,
								})
							);
						}
					}
					break;

				case DatasetStepEnum.STEP_DATASET_SPECS:
					if (
						(await confirmSpecsChanges())?.moveToNextStep
					) {
						// Follow the confirmed backend step, including Quick start.
						await moveToNextStep();
					}
					break;

				case DatasetStepEnum.STEP_DATASET_APPROVAL: {
					const confirmation = await dispatch(
						confirmDatasetStatsThunk(activeDatasetId)
					);
					if (
						confirmDatasetStatsThunk.fulfilled.match(
							confirmation
						)
					) {
						await moveToNextStep();
					} else {
						toast.error("Approval could not be completed.");
					}
					break;
				}

				default:
					break;
			}
			setTimeout(() => {
				setLoadingContinue(false);
			}, 500);
		}
	}, [
		activeDatasetMeta,
		activeStep,
		staticDataRangeSelection?.recentRangeSelection,
		isDataRangeModified,
		perfDataRangeSelection?.recentRangeSelection,
		confirmSpecsChanges,
		isUserInputChanged,
		reloadDataset,
		dispatch,
		moveToNextStep,
		isReadOnlyUser,
	]);

	useEffect(() => {
		if (
			submissionStatus?.submissionCreated &&
			submissionStatus.isSubmissionSuccessful
		) {
			switch (submissionStatus.currentStep) {
				case DatasetStepEnum.STEP_STATIC_DATA_RANGE: {
					toast.success("Static data extraction completed");
					break;
				}
				case DatasetStepEnum.STEP_PERF_DATA_RANGE: {
					toast.success("Perf data extraction completed");
					break;
				}
				case DatasetStepEnum.STEP_DATASET_SPECS: {
					toast.success("Dataset specs completed");
					break;
				}

				default: {
					toast.success("Submission successful");
					break;
				}
			}
			void moveToNextStep();
		} else if (
			submissionStatus?.submissionCreated &&
			!submissionStatus.isSubmissionSuccessful
		) {
			// Handle error
			toast.error(
				(submissionStatus.error?.msg as string) ||
					"Error occurred while submitting your response!"
			);
			dispatch(
				LedgerActions.updateSanitizationSubmissionStatus(
					null
				)
			);
		}
	}, [dispatch, moveToNextStep, submissionStatus]);

	const shouldDisableEverything = useMemo(() => {
		if (isReadOnlyUser) {
			return true;
		}
		if (datasetSanitizationMeta?.isApproved) {
			return true;
		} else if (datasetSanitizationMeta === null) {
			return true;
		}
		return false;
	}, [datasetSanitizationMeta, isReadOnlyUser]);

	const value = {
		activeStep,
		setActiveStep,
		navigate,
		dispatch,
		activeDatasetMeta,
		staticDatasetUploadStatus,
		perfDatasetUploadStatus,
		uploadDataset,
		sheet,
		setSheet,
		headerRange,
		setHeaderRange,
		dataRange,
		setDataRange,
		disableContinue,
		setDisableContinue,
		loadingContinue,
		setLoadingContinue,
		isDataRangeModified,
		setIsDataRangeModified,
		isUserInputChanged,
		setIsUserInputChanged,
		shouldDisableEverything,
		isReadOnlyUser,
		handleContinue,
		moveToNextStep,
	};

	return (
		<LedgerContext.Provider value={value}>
			{children}
		</LedgerContext.Provider>
	);
};
